import * as XLSX from 'xlsx';

const getValue = (row, key) => {
  if (typeof key === 'function') return key(row);
  if (typeof key === 'string' && key.includes('.')) {
    return key.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), row);
  }
  return row?.[key];
};

/**
 * Construit une feuille (worksheet) à partir des lignes et colonnes.
 */
const buildSheet = (rows, columns) => {
  const header = columns.map(c => c.label);
  const data = rows.map(row => columns.map(c => {
    const raw = getValue(row, c.key);
    if (typeof c.format === 'function') return c.format(raw, row);
    if (raw == null) return '';
    return raw;
  }));
  const aoa = [header, ...data];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = columns.map((c, idx) => {
    if (c.width) return { wch: c.width };
    const max = aoa.reduce((m, row) => {
      const v = row[idx];
      const len = v == null ? 0 : String(v).length;
      return len > m ? len : m;
    }, 0);
    return { wch: Math.min(Math.max(max + 2, 10), 50) };
  });
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
    if (cell) cell.s = { font: { bold: true } };
  }
  return ws;
};

const downloadWorkbook = (wb, filename) => {
  const today = new Date();
  const stamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  XLSX.writeFile(wb, `${filename}_${stamp}.xlsx`);
};

/**
 * Exporte une liste de lignes vers un fichier Excel téléchargeable (1 onglet).
 *
 * @param {Object[]} rows - Données sources (tableau d'objets).
 * @param {Object[]} columns - Définition des colonnes : [{ key, label, format?, width? }].
 * @param {string} sheetName - Nom de l'onglet Excel (≤ 31 chars).
 * @param {string} filename - Nom du fichier (sans extension).
 */
export const exportToExcel = (rows, columns, sheetName, filename) => {
  const ws = buildSheet(rows, columns);
  const wb = XLSX.utils.book_new();
  const safeSheetName = (sheetName || 'Export').substring(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
  downloadWorkbook(wb, filename);
};

/**
 * Exporte plusieurs jeux de données dans UN SEUL fichier Excel multi-onglets.
 *
 * @param {Object[]} sheets - [{ rows, columns, sheetName }]
 * @param {string} filename - Nom du fichier (sans extension).
 */
export const exportMultiSheetExcel = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  let appended = 0;
  sheets.forEach((s, i) => {
    if (!s || !Array.isArray(s.rows)) return;
    const ws = buildSheet(s.rows, s.columns);
    const name = (s.sheetName || `Feuille ${i + 1}`).substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, name);
    appended += 1;
  });
  if (appended === 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Aucune donnée']]), 'Export');
  }
  downloadWorkbook(wb, filename);
};

/**
 * Format helpers réutilisables
 */
export const fmtEurExcel = (v) => Number(v || 0); // Nombre brut → Excel formatera
export const fmtDateExcel = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
